/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedNumber, useIntl} from 'react-intl'
import {remCalc} from '../../theme/_utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {isPecualiarAddressFormCountry} from '../../utils/peculiar-address-form'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const JPAddressDisplay = ({
    address,
    styles,
    confirmationGap,
    nameStyle,
    isConfirmation,
    isBilling,
    isAccount,
    isCheckout,
    isB2B,
    shippingMethod,
    currency,
    formatMessage,
}) => {
    return (
        <Flex {...styles.addressContainer} style={confirmationGap} direction="column">
            {isCheckout ? (
                <>
                    {isB2B ? (
                        <>
                            {address?.c_companyName && (
                                <Text style={nameStyle}>{address.c_companyName}</Text>
                            )}
                            {(address?.c_vatNumber || address?.c_sdi) && (
                                <Text>
                                    {address?.c_vatNumber}, {address?.c_sdi}
                                </Text>
                            )}

                            <Text>
                                {isConfirmation && !isBilling && !isAccount ? (
                                    <>
                                        {address?.firstName} {address?.lastName}
                                    </>
                                ) : (
                                    <>
                                        {address?.lastName} {address?.firstName}
                                    </>
                                )}
                            </Text>

                            <Text>{address.postalCode}</Text>
                            <Text>
                                {address.c_regionName}, {address.city}
                            </Text>
                            <Text>{address.address1}</Text>
                            {address.address2 && <Text>{address.address2}</Text>}
                        </>
                    ) : (
                        <>
                            <Flex justify="space-between" align="center" width="100%">
                                <Text>
                                    {isConfirmation && !isBilling && !isAccount ? (
                                        <>
                                            {address?.firstName} {address?.lastName}
                                        </>
                                    ) : (
                                        <>
                                            {address?.lastName} {address?.firstName}
                                        </>
                                    )}
                                </Text>
                                {shippingMethod &&
                                    (shippingMethod.price === 0 ? (
                                        <Text
                                            fontSize="sm"
                                            fontWeight="bold"
                                            color="green.500"
                                            textTransform="uppercase"
                                        >
                                            {formatMessage({
                                                defaultMessage: 'Free',
                                                id: 'checkout_confirmation.label.free',
                                            })}
                                        </Text>
                                    ) : (
                                        <Text fontSize="sm" fontWeight="bold">
                                            <FormattedNumber
                                                value={shippingMethod.price}
                                                style="currency"
                                                currency={currency}
                                            />
                                        </Text>
                                    ))}
                            </Flex>
                        </>
                    )}
                    {address?.address2 && (
                        <Text textColor={'text.secondary'}>{address?.address2}</Text>
                    )}
                    <Flex justify="space-between" align="center" width="100%">
                        <Text textColor={'text.secondary'}>
                            {address?.postalCode} {address?.c_regionName}, {address?.city},{' '}
                            {address?.address1}
                        </Text>
                        {shippingMethod?.c_estimatedArrivalTime && (
                            <Text fontSize="sm" textColor={'text.secondary'}>
                                {shippingMethod.c_estimatedArrivalTime}
                            </Text>
                        )}
                    </Flex>
                </>
            ) : (
                <>
                    {address?.c_fiscalCode && <Text mb={remCalc(16)}>{address.c_fiscalCode}</Text>}
                    {address?.c_companyName && <Text>{address.c_companyName}</Text>}
                    {address?.c_vatNumber && <Text>{address.c_vatNumber}</Text>}
                    {address?.c_sdi && <Text>{address.c_sdi}</Text>}
                    {(address?.companyName || address?.c_vatNumber || address?.c_sdi) && (
                        <>&nbsp;</>
                    )}
                    <Text style={nameStyle}>
                        {isConfirmation && !isBilling && !isAccount ? (
                            <>
                                {address?.firstName} {address?.lastName}
                            </>
                        ) : (
                            <>
                                {address?.lastName} {address?.firstName}
                            </>
                        )}
                    </Text>
                    <Text>{address?.phone}</Text>
                    <Text>{address?.c_countryName}</Text>
                    <Text>{address?.postalCode}</Text>
                    <Text>
                        {' '}
                        {address?.c_regionName}, {address?.city}{' '}
                    </Text>
                    {address?.c_PickupPoint_Name ? (
                        <Text>
                            {address?.c_PickupPoint_Name} - {address?.address1}
                        </Text>
                    ) : (
                        <Text>{address?.address1}</Text>
                    )}
                </>
            )}
        </Flex>
    )
}

const DefaultAddressDisplay = ({
    address,
    styles,
    confirmationGap,
    checkoutMargin,
    nameStyle,
    isCheckout,
    isB2B,
    shippingMethod,
    currency,
    formatMessage,
}) => (
    <Flex {...styles.addressContainer} style={(confirmationGap, checkoutMargin)} direction="column">
        {isCheckout ? (
            <>
                {isB2B ? (
                    <>
                        {address?.c_companyName && <Text>{address.c_companyName}</Text>}
                        {(address?.c_vatNumber || address?.c_sdi) && (
                            <Text>
                                {address?.c_vatNumber}, {address?.c_sdi}
                            </Text>
                        )}
                        <Text>
                            {address?.firstName} {address?.lastName}
                        </Text>
                    </>
                ) : (
                    <>
                        <Flex justify="space-between" align="center" w="100%">
                            <Text>
                                {address?.firstName} {address?.lastName}
                            </Text>
                            {shippingMethod &&
                                (shippingMethod.price === 0 ? (
                                    <Text
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color="green.500"
                                        textTransform="uppercase"
                                    >
                                        {formatMessage({
                                            defaultMessage: 'Free',
                                            id: 'checkout_confirmation.label.free',
                                        })}
                                    </Text>
                                ) : (
                                    <Text fontSize="sm" fontWeight="bold">
                                        <FormattedNumber
                                            value={shippingMethod.price}
                                            style="currency"
                                            currency={currency}
                                        />
                                    </Text>
                                ))}
                        </Flex>
                    </>
                )}
                {address?.address2 && <Text>{address?.address2}</Text>}
                <Flex justify="space-between" align="center" w="100%">
                    <Text textColor={'text.secondary'}>
                        {address?.address1}, {address?.city}, {address?.c_regionName},{' '}
                        {address?.postalCode}
                    </Text>
                    {shippingMethod?.c_estimatedArrivalTime && (
                        <Text fontSize="sm" textColor={'text.secondary'}>
                            {shippingMethod.c_estimatedArrivalTime}
                        </Text>
                    )}
                </Flex>
            </>
        ) : (
            <>
                {address?.c_fiscalCode && <Text mb={remCalc(16)}>{address.c_fiscalCode}</Text>}
                {address?.c_companyName && <Text>{address.c_companyName}</Text>}
                {address?.c_vatNumber && <Text>{address.c_vatNumber}</Text>}
                {address?.c_sdi && <Text>{address.c_sdi}</Text>}
                {(address?.c_companyName || address?.c_vatNumber || address?.c_sdi) && <>&nbsp;</>}
                <Text style={nameStyle}>
                    {address?.firstName} {address?.lastName}
                </Text>
                <Text>{address?.phone}</Text>
                {address?.c_PickupPoint_Name ? (
                    <Text>
                        {address?.c_PickupPoint_Name} - {address?.address1}
                    </Text>
                ) : (
                    <Text>{address?.address1}</Text>
                )}

                {address?.address2 && <Text>{address?.address2}</Text>}
                <Text>
                    {address?.city}, {address?.c_regionName}, {address?.postalCode}
                </Text>
            </>
        )}
    </Flex>
)

const AddressDisplay = ({
    address,
    isConfirmation = false,
    isTitleBold = false,
    isBilling = false,
    isAccount = false,
    isCheckout = false,
    shippingMethod = null,
}) => {
    const {formatMessage} = useIntl()
    const {currency} = useCurrency()
    const nameStyle = (isConfirmation && isTitleBold) || isCheckout ? {fontWeight: 'bold'} : {}
    const confirmationGap = isConfirmation ? {gap: remCalc(4)} : {}
    const styles = useMultiStyleConfig('AddressDisplay', {})
    const {locale} = useMultiSite()
    const isPeculiarAddressFormCountry = isPecualiarAddressFormCountry(locale)
    const checkoutMargin = isCheckout ? {marginTop: isBilling ? remCalc(6) : remCalc(4)} : {}
    const isB2B = !!address?.c_companyName
    return isPeculiarAddressFormCountry ? (
        <JPAddressDisplay
            address={address}
            styles={styles}
            confirmationGap={confirmationGap}
            nameStyle={nameStyle}
            isConfirmation={isConfirmation}
            isBilling={isBilling}
            isAccount={isAccount}
            isB2B={isB2B}
            shippingMethod={shippingMethod}
            currency={currency}
            formatMessage={formatMessage}
        />
    ) : (
        <DefaultAddressDisplay
            address={address}
            styles={styles}
            confirmationGap={confirmationGap}
            nameStyle={nameStyle}
            checkoutMargin={checkoutMargin}
            isCheckout={isCheckout}
            isB2B={isB2B}
            shippingMethod={shippingMethod}
            currency={currency}
            formatMessage={formatMessage}
        />
    )
}

JPAddressDisplay.propTypes = {
    address: PropTypes.object,
    styles: PropTypes.object,
    confirmationGap: PropTypes.object,
    nameStyle: PropTypes.object,
    isConfirmation: PropTypes.bool,
    isBilling: PropTypes.bool,
    isAccount: PropTypes.bool,
    checkoutMargin: PropTypes.object,
    isCheckout: PropTypes.bool,
    isB2B: PropTypes.bool,
    shippingMethod: PropTypes.object,
    currency: PropTypes.string,
    formatMessage: PropTypes.func,
}

DefaultAddressDisplay.propTypes = {
    address: PropTypes.object,
    styles: PropTypes.object,
    confirmationGap: PropTypes.object,
    nameStyle: PropTypes.object,
    checkoutMargin: PropTypes.object,
    isCheckout: PropTypes.bool,
    isB2B: PropTypes.bool,
    shippingMethod: PropTypes.object,
    currency: PropTypes.string,
    formatMessage: PropTypes.func,
}

AddressDisplay.propTypes = {
    address: PropTypes.object,
    isConfirmation: PropTypes.bool,
    isTitleBold: PropTypes.bool,
    isBilling: PropTypes.bool,
    isAccount: PropTypes.bool,
    isCheckout: PropTypes.bool,
    shippingMethod: PropTypes.object,
}

export default AddressDisplay
